import {gsap} from "gsap/all";
import $ from "jquery";
import {BaseGameMars} from "../base/BaseGameMars";
import {GroundDistanceGauge} from "./GroundDistanceGauge";
import {SpeedGauge} from "./SpeedGauge";
import {Game} from "./Game";
import {ShipHeatGlow} from "./ShipHeatGlow";
import {DustCloud} from "./DustCloud";
import {ActionDemo} from "./actions/ActionDemo";
import {FrameAnimation} from "./FrameAnimation";
import {ThrusterGlow} from "../ui/ThrusterGlow";
import {SOUNDS} from "./Sounds";

// The Entry, Descent and Landing interactive: the intro of screen 1, the flight
// itself on screen 2 (run by the Game class) and the mission report of screen 3.
// This class owns everything around the gameplay, the console input, the stage
// backgrounds, the one ship, the two gauges and the transitions between actions.
export class EDL extends BaseGameMars {

  // The haze the atmosphere comes in at before it thickens into a solid sky over
  // the descent, and how long that whole ride lasts when nothing cuts it short.
  static SKY_HAZE = 0.65;
  static SKY_RIDE_SEC = 90;

  // The sequences that wait hidden for their turn, each one taking the frame
  // over from the one before it. They draw the same ship on the same canvas, so
  // the hand-over does not show.
  static SEQUENCE_TAKEOVERS = [
    {from: "deorbit-burn", to: "deploy-heat-shield"},
    {from: "deploy-heat-shield", to: "descent-thruster-doors"},
    {from: "descent-thruster-doors", to: "descent-landing-legs"},
    {from: "descent-landing-legs", to: "deflate-heat-shield"},
    {from: "deflate-heat-shield", to: "crew-landed"}
  ];

  // The pause between the animations that close the run: after the shield
  // deflates, and after the crew comes out.
  static ENDING_HOLD_SEC = 2;

  // The action the speed dial belongs to. Speed only starts mattering once the
  // ship is being aligned in the air, so the dial stays out of the screen until
  // that action opens and is in for the rest of the run.
  static SPEED_GAUGE_ACTION_ID = 4;

  // Actions whose background is not taken automatically on the way in. Theirs
  // waits for whatever is still playing on the one before it, down in
  // beforeAction.
  static SELF_STAGED_ACTIONS = [3, 4, 5];

  // Where the entry is flown, in screen pixels: the middle of the align target,
  // the size the ship is drawn at there and the angle it comes in on. Measured
  // off the design of that screen, where the capsule was drawn into the target
  // at scale 0.2966 of its 3769 frame, turned -30.87 degrees and centered on
  // 1028.5, 1743.5 of a 2160 by 3588 viewBox that renders 1:1 in a 3840 tall
  // screen, so everything sits 126 lower than its own coordinates say. The
  // capsule is taken from the burn onto these and the descent grows back out
  // of them, which is why nothing is ever drawn twice.
  static ENTRY_CAPSULE = {
    centerX: 1028.5,
    centerY: 1869.5,
    frameWidth: 1117.9,
    rotation: -30.87
  };

  // The throttle the two outer engines idle at from the descent on. The
  // stabilize action pushes whichever side fights the tilt back to a full burn,
  // so they light straight to this rather than coming up full and dropping.
  static SIDE_THRUSTER_IDLE = 0.25;

  // The loops the "still there?" modal holds while it is up, everything but
  // the background noise.
  static PAUSED_LOOPS = [SOUNDS.idle, SOUNDS.fire, SOUNDS.thrusters];

  // How long a side takes to travel between that idle and a full burn. Slow
  // enough that the flame is seen growing and shrinking instead of snapping.
  static SIDE_THRUSTER_RAMP_SEC = 0.8;

  // How long the engines take to settle on the same throttle as the stabilize
  // run ends, and that throttle, 1 being a full burn. They wind down to
  // nothing from there. See startLandingThrottle.
  static LANDING_THROTTLE_SETTLE_SEC = 2;
  static LANDING_THRUST = 0.4;

  // Dev shortcut, see jumpIntoRun. What the ship looks like when the run opens
  // straight on an action: which animation it holds, and whether its engines
  // are on. Anything not listed opens with the shield out and engines off.
  static JUMP_SHIP_STATE = {
    default: {sequence: "deploy-heat-shield", thrusters: false},
    7: {sequence: "descent-landing-legs", thrusters: true}
  };

  // The sky moves for the whole descent, and a jump skips the part of the run
  // that moves it. So it is put three quarters of the way along and given
  // twenty seconds for the rest, which is about where a played run leaves it.
  static JUMP_SKY = {progress: 0.75, remaining_sec: 20};

  // How long the landing lasts, from the ground showing up at the bottom of
  // the screen to the ship standing on it.
  static LANDING_SEC = 8;

  // How long the ship takes to shrink to its landing size before the ground
  // shows up, see shrinkLandingShip.
  static LANDING_SHRINK_SEC = 1.2;

  // The shadow the ship casts on the surface as it lands. It says nothing while
  // the ship is still high, so it only comes in partway down: startAt is where
  // in the fall it appears, scaleFrom how small it is at that moment, and it is
  // full size and full strength exactly when the legs touch. Its resting size
  // and where it sits on the ground are in background.scss.
  //
  // deflatedScale is what it settles to once the shield packs away: the shield
  // is most of what the standing ship puts between the sun and the ground, so
  // the shadow tightens with it.
  static LANDING_SHADOW = {startAt: 0.35, scaleFrom: 0.3, deflatedScale: 0.82};

  // The dust the engines blow off the ground, as shares of the fall like the
  // shadow above: it starts being kicked up at startAt, is at its thickest at
  // peakAt, and stops being blown as the engines die at the touchdown. What is
  // already in the air then drifts on and settles. The look of the cloud is
  // in DustCloud, tunable with ?dustDebug.
  static LANDING_DUST = {startAt: 0.35, peakAt: 0.85};

  // How far the ship still falls during the landing, in screen pixels. Small on
  // purpose: most of the fall is the ground coming up, not the ship going down.
  // Lower it to land higher on the screen, raise it to land lower.
  static LANDING_DROP = 390;

  // Where the ground starts from, in screen pixels away from where it rests: a
  // full height below the screen so it rises into view, and to the right so it
  // comes in from the bottom right corner. The art hangs 1498 pixels off the
  // left of the screen, so x has to stay under that or its left edge shows.
  // Going higher means cropping less off the art, see .mars-ground in
  // background.scss. Used by landOnMars.
  static GROUND_ENTRY = {x: 1200, y: 2268};

  // The slowest the background is allowed to move while the run is still going.
  // The dial reads zero before the ship has actually touched down, and a
  // background standing still while the ground is still coming up looks broken,
  // so only the landing takes it all the way to a stop.
  static BACKGROUND_SPEED_MIN = 0.15;

  // What full pace actually means, on top of the level read off the dial. The
  // descent loses so much speed that the last steps were left crawling, so the
  // whole run is played faster while the slowdowns between the steps keep the
  // spacing they were authored with. Raise it to move the sky faster
  // everywhere, without touching the drifts or the curve they are read on.
  static BACKGROUND_SPEED_SCALE = 2;

  // How long the stars and the arcs take to fade in at the detach. They are
  // already travelling at full pace underneath, see revealSpace.
  static SPACE_REVEAL_SEC = 1.5;

  // The pace the sky rushes past at from the detach until the deorbit burn
  // slows it down, 1 being full pace. Above 1 on purpose: the capsule is still
  // in orbit, the fastest it ever goes, and the dial is not showing yet to say
  // otherwise.
  static DETACH_BACKGROUND_SPEED = 2;

  // How long the transit vehicle takes to leave the screen once the capsule
  // has detached from it, see startMTVDetach.
  static MTV_EXIT_SEC = 8;

  // The pace the sky slows to over the deorbit burn, 1 being full pace, when the
  // transition of that action does not give its own background_speed. Keep it
  // above the pace the align action ends on, or the sky would speed back up
  // there.
  static DEORBIT_BURN_BACKGROUND_SPEED = 1.25;

  // The three descent engines, hottest to coolest, which is the walk a puff of
  // the burn makes as it ages. A puff spends an equal share of its life on each
  // of these. No white in the list: the burn is cyan from the nozzle out, and
  // what reads as a white core is the hottest of these stacked on itself where
  // the puffs are densest. The three cooler steps are the exhibit palette,
  // $sky-blue, $blue and $earth-blue in variables.scss. The core on the nozzle
  // is drawn from the first two, so it cools with the list.
  //
  // Only EDL burns cold like this, the flight to Mars keeps the warm palette
  // ThrusterGlow ships with.
  static THRUSTER_PALETTE = ["#45F2F2", "#61E5F9", "#49BACD", "#1F7F88"];

  // The stars breathe while the ship is still docked and the sky is standing
  // still. Dimming the whole field at once reads as the screen flickering, so
  // the blink is done in patches instead: each of the patches over the stars
  // fades in and out on its own, and since they never line up, parts of the sky
  // are always going dark while others are coming back. It runs from boot until
  // the detach sets the sky moving.
  //
  // opacity is the band a patch travels, 1 being its stars fully out: it rests
  // at the min and every blink takes it up to the max and back down, so that is
  // the whole range the sky is ever seen in. Every blink picks its own pace and
  // its own wait before the next one, so no patch falls into a rhythm and they
  // never drift into step with each other.
  static STAR_TWINKLE = {
    opacity: {min: 0, max: 0.9},
    duration: {min: 0.6, max: 1.6},
    pause: {min: 0.2, max: 2.4}
  };

  constructor() {
    super(`data/edl.json`, 'edl');

    this.currentStage = null;

    // The two endless sky drifts, held here so the ramp bringing them up to
    // speed can be called off when a run ends. The sky travel is held next to
    // them because it is played at the same pace, see setBackgroundSpeed.
    this.backgroundDrift = null;
    this.skyRise = null;
    this.backgroundSpeed = 1;

    // The timelines blinking the patches over the star field, one per patch.
    this.starTwinkles = null;

    // The transit vehicle only ever detaches once per run, and the shield only
    // ever deploys once.
    this.mtvDetaching = false;
    this.shieldDeploying = false;
    this.shieldDeployed = null;

    // Built from the template in initThrusters, empty until then so a reset
    // before the game is up has nothing to do.
    this.thrusters = [];
    this.thrustersBySide = {};
    this.currentTransitionTimeline = null;

    // Held state per action, tracked separately since the keyboard allows
    // holding both arrows at once.
    this.heldActions = {};

    // The exported root timeline holding the whole run while the "still
    // there?" modal is up, null when the run is playing.
    this.pausedGameplay = null;

    this.keyboardEnabled = false;
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
  }

  initGame() {
    this.enableKeyboard();

    // PNG sequences declared in the template, paused on their first frame until
    // something plays them. See getAnimation below.
    FrameAnimation.initAll();

    this.initThrusters();

    // Distance travelled towards the ground, empty at the top and full once
    // the last action of the sequence is done. Only shown during the run.
    this.groundDistanceGauge = new GroundDistanceGauge($("#ground-distance-gauge"));
    this.groundDistanceGauge.reset(this.getGaugeStartState());

    // Speed of the ship, riding along with the same states. The dial itself
    // stays out of the screen until the align action brings it in, see
    // showSpeedGauge.
    this.speedGauge = new SpeedGauge($("#speed-gauge"));
    this.speedGauge.reset(this.getSpeedStartState());
    this.showSpeedGauge(false);

    // The air burning around the ship. One effect for the whole run, living
    // inside the assembly, so it follows the ship through every move it makes
    // and never has to be handed over to a second copy of itself. Built on the
    // way into the entry and running from there until the descent puts it out.
    this.shipHeatGlow = new ShipHeatGlow(this.getShipAssembly());

    // The dust the engines blow off the ground as the ship lands, centered on
    // the shadow so it rides up with the surface. See dropOntoMars.
    this.dustCloud = new DustCloud($(".mars-dust.back"), $(".mars-dust.front"), this.getMarsGroundShadow());

    this.game = new Game(this);

    this.timingDemo = new ActionDemo(
      $("#screen-1 .step[data-step='2'] .action-container .action[data-action='timing']"),
      ["#325FD5", "#1587D9", "#8BC9E7"]
    );
    this.timingDemo.buildTiming();

    this.memoryDemo = new ActionDemo(
      $("#screen-1 .step[data-step='2'] .action-container .action[data-action='memory-slot']")
    );
    this.memoryDemo.buildMemory(["sq_orange", "sq_blue", "sq_yellow"], [true, true, true]);

    // Only the first stage layers visible at boot.
    this.setStage(1, true);

    this.preloadHeavyArt();

    this.startStarTwinkle();

    // The background noise plays for as long as the app runs, at a volume of
    // its own, the idle loop until the run reaches the detach.
    this.audio.playLoop(SOUNDS.background, this.getSoundVolume("background"), 3);
    this.audio.playLoop(SOUNDS.idle, this.getSoundVolume("idle"));

    super.initGame();

    this.showTriggerKeys();
    this.initStillThereSounds();
  }

  // One beat of the animation that plays after an action, in seconds, from the
  // transition block of that action in the JSON:
  //
  //   "transition": {"light_sec": 0.8, "hold_sec": 1}
  //
  // Which beats a transition has is up to the transition, they are read one by
  // one down in beforeAction where each is used. Every one is optional, and the
  // fallback here is the timing the run had before the JSON had a say.
  getTransitionSec(config, name, fallback) {
    const timing = (config && config.transition) || {};

    return timing[name] !== undefined ? timing[name] : fallback;
  }

  // Where the gauges end up after an action, moved over the animation that plays
  // between that action and the next one instead of before it. The travel lasts
  // as long as the animation, so they land right as the next action opens,
  // unless the state names a duration of its own.
  //
  // except names fields of the state to leave out, for a transition that swaps
  // them on a beat of its own. ease replaces the one of the state, for a
  // travel done in several legs.
  applyTransitionGauge(previousConfig, duration, except = [], ease = null) {
    const state = this.getTransitionGaugeState(previousConfig);
    if (!state) return;

    const applied = {...state};
    except.forEach((field) => delete applied[field]);

    if (ease) {
      applied.ease = ease;
      if (applied.speed) applied.speed = {...applied.speed, ease};
    }

    this.setGaugeState(applied, state.duration !== undefined ? state.duration : duration);
  }

  // The state the animation after an action travels to: the end state of the
  // action itself when the JSON marks it during_transition (see
  // Action.applyGaugeEnd), else a gauge_at_transition_end of its own, for an
  // action whose end state already played while it was up. The stabilize action
  // is the second kind: it ends at 65 ft and 5.5 mph as it is played, and the
  // landing after it takes both down to zero.
  getTransitionGaugeState(config) {
    if (!config) return null;

    const end = config.gauge_at_sequence_end;
    if (end && end.during_transition) return end;

    return config.gauge_at_transition_end || null;
  }

  // How long one of those sequences takes to play, in seconds, 0 for one the
  // page does not have.
  getAnimationSec(name) {
    const animation = this.getAnimation(name);

    return animation ? animation.getDuration() : 0;
  }

  // One of the PNG sequences in the page, by its data-name. Play it from an
  // action with await this.app.getAnimation("deploy-heat-shield").play().
  getAnimation(name) {
    return FrameAnimation.getByName(name);
  }

  actionForKeypress(key) {
    const entry = this.getPhidgetMap().find((entry) => entry.keypress === key);
    return entry ? entry.action : null;
  }

  keypressForAction(action) {
    const entry = this.getPhidgetMap().find((entry) => entry.action === action);
    return entry ? entry.keypress : null;
  }

  // For dev the keyboard stands in for the console, using the keypress of each
  // phidget map entry. Presses and releases both go through onMessage, the same
  // way the hardware reports its state changes.
  enableKeyboard() {
    if (this.keyboardEnabled) return;
    this.keyboardEnabled = true;
    $(document).on("keydown.edl-input", this.onKeyDown);
    $(document).on("keyup.edl-input", this.onKeyUp);
  }

  onKeyDown(e) {
    // Ignore the auto-repeat of a held key, a press is a single event.
    if (e.originalEvent && e.originalEvent.repeat) return;

    this.onMessage(this.actionForKeypress(e.key), true);
  }

  onKeyUp(e) {
    this.onMessage(this.actionForKeypress(e.key), false);
  }

  // Current joystick direction as -1, 0 or 1. The modal owns the console while
  // it is up, so the run behind it reads a centered joystick.
  getAxis() {
    if (this.isStillThereActive()) return 0;

    return (this.heldActions["joy_right"] ? 1 : 0) - (this.heldActions["joy_left"] ? 1 : 0);
  }

  // The buttons of the console are its round and square ones, the joystick
  // entries are the held directions read by getAxis.
  isButtonAction(action) {
    const entry = this.getPhidgetMap().find((entry) => entry.action === action);
    return !!entry && entry.type === "button";
  }

  // Single entry point for the console, from the hardware or from the keyboard
  // above: an action name of the phidget map and whether it was pressed or
  // released. The joystick only updates its held state, buttons report their
  // press.
  onMessage(action, pressed = true) {
    // Any press or release counts as activity, the same way a tap anywhere on
    // the screen does, so this comes before the unmapped input is dropped. The
    // hardware bridge already reset the timer, this covers the keyboard
    // standing in for the console.
    this.resetTimer();

    if (!action) return;

    this.heldActions[action] = pressed;
    if (!pressed || !this.isButtonAction(action)) return;

    // The black button is the way out, from anywhere, modal included.
    if (action === "round_black") {
      this.pressBlackButton();
      return;
    }

    // The "still there?" modal owns the console while it is up, so a press
    // answers the modal and never reaches the run behind it.
    if (this.isStillThereActive()) {
      this.pressStillThereButton(action);
      return;
    }

    // The action mounted in the game takes the press, and outside of the
    // gameplay it stands in for a screen tap.
    if (this.game && this.game.currentAction) {
      this.game.currentAction.onButtonPress(action);
      this.onGameButtonPress(this.game.currentAction);
    }

    this.pressToTrigger(action);
  }

  // Straight back to the idle screen. With the modal up it closes the game the
  // same way the countdown running out does. Ignored on the idle screen itself
  // and during a screen change, so a second press can't start a second reset.
  pressBlackButton() {
    if (this.lockScreen) return;

    if (this.isStillThereActive()) {
      this.screenSaverCloseGame();
      return;
    }

    if (this.currentScreen === 1 && this.currentStep === 1) return;

    this.closeGame();
  }

  isStillThereActive() {
    return $("#still-there").hasClass("active");
  }

  // The modal freezes the run behind it, and lets it go once it has faded out,
  // whether the player kept playing or the game was closed.
  onStillThereOpened() {
    this.pauseGameplay();
    EDL.PAUSED_LOOPS.forEach((sound) => this.audio.pause(sound, 0.5));
  }

  onStillThereClosed() {
    this.resumeGameplay();
    EDL.PAUSED_LOOPS.forEach((sound) => this.audio.resume(sound, 0.5));
  }

  // The volume of one group of sounds, <name>_volume in the JSON:
  // background_volume, idle_volume, buttons_volume (every press, verdict, start
  // and end sound), fire_volume, thrusters_volume and ship_volume (the heat
  // shield, the thruster doors and the landing legs). A group the JSON leaves
  // out plays at full volume. The volume of settings.json is the master volume
  // applied over every group, so the whole mix can be turned down onsite.
  getSoundVolume(name) {
    const volume = this.data[`${name}_volume`];
    const groupVolume = typeof volume === "number" ? volume : 1;
    return groupVolume * this.getVolume();
  }

  // Freezes the run where it stands. A run is spread over a lot of separate
  // animations (the action tweens, the pauses between them, the gauges, the
  // stage transitions, the stars), so rather than chase each one, this pauses
  // them all at once through the timeline they already share.
  //
  // Every tween and delayed call GSAP creates is a child of one global
  // timeline. Pausing that timeline itself would be too much: it would freeze
  // the modal too, and anything created afterwards would be born paused.
  // exportRoot moves every child that exists right now into a new timeline of
  // its own and hangs that one back on the global timeline:
  //
  //   before   global -> [action tween, gauge tween, delayed call, stars]
  //   after    global -> [exported -> [action tween, gauge tween, ...]]
  //
  // Pausing "exported" holds that whole group with each playhead kept where it
  // was, and resume picks them all up from there. Anything built after the
  // export, the modal and its countdown, lands on the global timeline next to
  // it and keeps animating. The pieces of the run driven by the ticker rather
  // than by a tween are not children of any timeline, so they read
  // isGameplayPaused instead.
  pauseGameplay() {
    // Exporting twice would nest a second timeline inside the first and the
    // resume below would only ever release the outer one.
    if (this.pausedGameplay) return;

    // GSAP stores a delayed call as a zero duration tween and leaves those out
    // of the export by default. The second argument takes them in, which the
    // game needs since its waits between actions are delayed calls.
    this.pausedGameplay = gsap.exportRoot({}, true);
    this.pausedGameplay.pause();

    if (this.shipHeatGlow) this.shipHeatGlow.setPaused(true);
    if (this.dustCloud) this.dustCloud.setPaused(true);
  }

  // The exported timeline stays on the global one until it is let go of, so a
  // pause that is never resumed leaves the app frozen for good. Both ways out
  // of the modal come through here, the close game one included, where the run
  // was already reset under the veil and there is nothing left to pick up.
  resumeGameplay() {
    if (!this.pausedGameplay) return;

    this.pausedGameplay.resume();
    this.pausedGameplay = null;

    if (this.shipHeatGlow) this.shipHeatGlow.setPaused(false);
    if (this.dustCloud) this.dustCloud.setPaused(false);
  }

  isGameplayPaused() {
    return this.pausedGameplay !== null;
  }

  // The two pills of the modal each name the console button they answer to,
  // blue to keep playing and pink to close the game. Any other button does
  // nothing, but it still kept the idle timer alive above.
  pressStillThereButton(action) {
    const $button = $(`#still-there .trigger[data-console-action="${action}"]`).not(".disabled").first();
    if (!$button.length) return;

    $button.trigger(this.touchEvent.split(" ")[0]);
  }

  // Both choices of the modal sound like a right press, the same as the end
  // screen ones, whether tapped or pressed on the console (which triggers the
  // same event, see pressStillThereButton). Filtered like the button
  // delegation of BaseGame, which runs right after this one, so the sound only
  // plays when the press actually goes through.
  initStillThereSounds() {
    $("#still-there").on(this.touchEvent, ".trigger", (e) => {
      if ($(e.currentTarget).is(".disabled")) return;
      if (e.type === "keydown" && !(e.keyCode === 13 || e.keyCode === 32)) return;
      if (this.currentAction !== false) return;

      this.audio.play(SOUNDS.rightInput, this.getSoundVolume("buttons"), true);
    });
  }

  // Dev help while the real console is not hooked up: every button the game
  // asks for also shows the keyboard key standing in for it. Turned off on the
  // deployed kiosk, where the player only ever sees the physical buttons.
  getShowConsoleKeys() {
    return this.settings.show_console_keys === true;
  }

  // A trigger answering to one console button in particular shows the key of
  // that button in the circle of its pill.
  showTriggerKeys() {
    if (!this.getShowConsoleKeys()) return;

    $("#main .trigger[data-console-action]").each((i, el) => {
      const $trigger = $(el);
      const keypress = this.keypressForAction($trigger.attr("data-console-action"));
      const $icon = $trigger.find(".icon");
      if (!keypress || !$icon.length) return;

      $icon.append($('<span class="dev-key"></span>').text(keypress));
    });
  }

  // A console press while no gameplay is running stands in for a screen tap on
  // a trigger element of the current screen (the press any button to start
  // kind of CTA, pill button or not). During gameplay the actions consume the
  // presses instead.
  pressToTrigger(action) {
    if (this.game && this.game.running) return;
    if (this.lockScreen) return;
    if (!this.$currentScreen || !this.$currentScreen.length) return;

    // Find the first available visible button
    const $triggers = this.$currentScreen
      .find(".trigger")
      .not(".disabled")
      .filter((i, el) => {
        const style = window.getComputedStyle(el);
        return style.visibility === "visible" && style.display !== "none" && parseFloat(style.opacity) > 0;
      });

    // A trigger naming a console button answers to that one only, which is how
    // the two choices of screen 3 tell themselves apart. The triggers naming
    // none answer to any button.
    const $named = $triggers.filter(`[data-console-action="${action}"]`).first();
    const $button = $named.length ? $named : $triggers.not("[data-console-action]").first();
    if (!$button.length) return;

    $button.trigger(this.touchEvent.split(" ")[0]);
  }

  initScreen(screenId) {
    const targetScreenNoSuffix = parseInt(screenId.toString().split("-")[0]);

    if (targetScreenNoSuffix === 2) {
      // The intro demos are behind us, they have no reason to keep looping.
      this.stopDemos();

      // This runs between the screen fades, so on a play again the background
      // snaps back to the first one behind a screen that is not showing yet.
      // First arrival from screen 1 is already on it, setStage skips it.
      this.setStage(1, true);

      // Coming back from the conclusion the ship layer was hidden, show it.
      gsap.to($("#ship-container"), {
        autoAlpha: 1,
        duration: 0.5
      });

      // The gauges belong to the flight, and the flight has not started: they
      // come in with the first action, at the end of the intro.
      this.showGauges(false, 0);
      this.playGameIntro();

    } else if (targetScreenNoSuffix === 3) {
      // The run is over by the time we get here, but a game left running would
      // eat the console presses meant for the two pill buttons.
      this.game.stop();

      // The screen transition just put screen 2 away to make room. It stays up
      // instead, under the conclusion coming in over it, and is only taken off
      // once it is fully covered. See goToConclusion for why.
      $("#screen-2").removeClass("hidden");
      gsap.set($("#screen-2"), {alpha: 1});

      // The screen fades in on its own background, with nothing on it. The
      // content rises into it after, see playConclusionIntro.
      gsap.set(this.getConclusionParts(), {
        autoAlpha: 0,
        y: 40
      });
    }
  }

  // The boxes of the conclusion, in the order they come in.
  getConclusionParts() {
    return $("#screen-3 .conclusion > *");
  }

  // Once the conclusion has covered the flight, its content arrives one piece
  // at a time, the same rise the actions of the run come in on.
  playConclusionIntro() {
    this.killTimeline(this.currentTimeline);

    this.currentTimeline = gsap.timeline();

    this.currentTimeline.to(this.getConclusionParts(), {
      autoAlpha: 1,
      y: 0,
      duration: 0.5,
      stagger: 0.15,
      ease: "power2.out"
    });
  }

  // The way into the conclusion. Nothing of the flight fades out: the gauges
  // carry glass, and glass inside a layer being faded stops refracting what is
  // behind it, so the whole screen would break on the way out. Instead the
  // conclusion fades in on top of it, its own background opaque and covering
  // the screen, and the flight is put away behind it once it is hidden.
  goToConclusion() {
    // The ship shuts down before the conclusion covers it: the shield deflates,
    // pause, the crew comes out, pause.
    this.playDeflateHeatShield()
      .then(() => this.wait(EDL.ENDING_HOLD_SEC))
      .then(() => this.playCrewLanded())
      .then(() => this.wait(EDL.ENDING_HOLD_SEC))
      .then(() => this.showConclusionScreen());
  }

  // The shield the ship has worn since the entry, packed away now that it has
  // landed.
  playDeflateHeatShield() {
    // The shadow on the ground loses what the shield was casting, over the same
    // stretch the shield takes to pack away.
    gsap.to(this.getMarsGroundShadow(), {
      scale: EDL.LANDING_SHADOW.deflatedScale,
      duration: this.getAnimationSec("deflate-heat-shield") || 1.2,
      ease: "power1.inOut"
    });

    return this.playTakeover("deflate-heat-shield");
  }

  // The crew stepping out onto Mars, the last animation of the run.
  playCrewLanded() {
    return this.playTakeover("crew-landed");
  }

  showConclusionScreen() {
    this.audio.play(SOUNDS.winGame, this.getSoundVolume("buttons"), true);

    this.goToScreen(3, () => {
      $("#screen-2").addClass("hidden");
      gsap.set($("#screen-2"), {alpha: 0});
      gsap.set($("#ship-container, #gauges-container"), {autoAlpha: 0});

      this.playConclusionIntro();
    }, false, {skipOut: true});
  }

  // The gauges belong to the flight, so they only ride along on screen 2 and
  // stay out of the intro and the conclusion. The tween comes back so a
  // sequence can hand it a spot of its own, which the intro does.
  showGauges(shown, duration = 0.5) {
    return gsap.to($("#gauges-container"), {
      autoAlpha: shown ? 1 : 0,
      duration,
      ease: "power1.inOut"
    });
  }

  // The speed dial on its own, which comes in later than the rest of the gauges,
  // see SPEED_GAUGE_ACTION_ID. It rises into place rather than simply fading, so
  // it reads as arriving with the align action.
  showSpeedGauge(shown, duration = 0.6) {
    const $speedGauge = $("#speed-gauge");
    gsap.killTweensOf($speedGauge);

    if (!shown) {
      gsap.set($speedGauge, {
        autoAlpha: 0,
        y: 40
      });

      return null;
    }

    return gsap.fromTo($speedGauge, {
      autoAlpha: 0,
      y: 40
    }, {
      autoAlpha: 1,
      y: 0,
      duration,
      ease: "power2.out"
    });
  }

  // The gauge state at the top of the descent, before the first action.
  getGaugeStartState() {
    return this.data.content.game.gauge_start;
  }

  // How the dial spreads the speed along its arc, see SpeedGauge.CURVE. From the
  // JSON, content.game.speed.curve, so it can be tuned on the kiosk.
  getSpeedCurve() {
    const speed = this.data.content.game.speed || {};

    return speed.curve !== undefined ? speed.curve : SpeedGauge.CURVE;
  }

  // Same for the speed dial, which the states carry in their speed object.
  getSpeedStartState() {
    return this.data.content.game.speed.start;
  }

  getSpeedMaxState() {
    return this.data.content.game.speed.max;
  }

  // Moves the ground distance gauge to one of the states a sequence action
  // defines: gauge_at_sequence_start when it launches, the gauge of a press as
  // that press resolves, gauge_at_sequence_end when it is done. All three are
  // optional and the actions drive them through the Action base class.
  //
  // Every field of the state is optional, so this also drives the gauge by hand
  // from anywhere the JSON cannot reach, a transition in beforeAction being the
  // usual one. The three helpers below are the same call, spelled shorter:
  //   this.setGaugePercent(40, 2);       // travel over the next two seconds
  //   this.setGaugeIcon("with-shield");  // swap the ship, leave the rest
  //   this.setGaugeReadout({fr: "", en: "ENTRY"}, 45);
  setGaugeState(state, duration = 1.2) {
    if (!state) return;

    if (this.groundDistanceGauge) this.groundDistanceGauge.setState(state, duration);

    // A state also carries where the speed dial sits at that moment, in its
    // speed object. Both gauges travel over the same duration unless the speed
    // object asks for its own.
    this.setSpeedState(state.speed, duration);
  }

  // The speed dial on its own, for the same by hand driving as the helpers
  // above: this.setSpeedState({value: 2.2}, 2);
  //
  // The background moves because the ship does, so it is played at whatever
  // speed the dial is reading and speeds up and slows down with it.
  setSpeedState(state, duration = 1.2) {
    if (state && state.value !== undefined) {
      this.setBackgroundSpeed(this.getBackgroundSpeedForDial(state.value), duration);
    }

    if (!this.speedGauge) return;
    this.speedGauge.setState(state, duration);
  }

  // The pace that matches a speed from the JSON, 1 being the fastest the run
  // ever goes. Never lower than the minimum above while the run is going.
  //
  // Read on the same curve as the dial, see SpeedGauge.CURVE, so the scenery and
  // the readout stay in step: read straight off the speed the background would
  // sit on the minimum from the first step of the run onwards while the dial
  // kept on falling, which makes the ship look slower than it says it is.
  getBackgroundSpeedForDial(value) {
    const max = this.getSpeedMaxState().value || 1;
    const level = this.speedGauge ? this.speedGauge.getLevelForValue(value) : value / max;

    return Math.max(level, EDL.BACKGROUND_SPEED_MIN);
  }

  // How fast the background moves, 1 being full pace. Nothing in the background
  // changes its own timing: the drifts have to run at a steady pace or they
  // would jump when they wrap, and the sky travel is aimed at a fixed spot. So
  // what changes is how fast they are played, all of them together.
  setBackgroundSpeed(level, duration = 1.2, ease = "power1.inOut") {
    this.backgroundSpeed = level;

    const movers = this.getBackgroundMovers();
    if (!movers.length) return;

    gsap.to(movers, {
      timeScale: this.getBackgroundTimeScale(),
      duration: duration,
      ease: ease,
      overwrite: "auto"
    });
  }

  // The rate the movers are actually played at: the level of the run, taken up
  // by the scale above. Everything driving a timeScale goes through here so the
  // sky never ends up half on the level and half on the played pace.
  getBackgroundTimeScale() {
    return this.backgroundSpeed * EDL.BACKGROUND_SPEED_SCALE;
  }

  // Everything moving in the background that is played at that pace: the stars,
  // the flight arcs and the sky travelling down to the ground.
  getBackgroundMovers() {
    return [...(this.backgroundDrift || []), this.skyRise].filter(Boolean);
  }

  // How far down the descent the gauge sits, 0 to 100.
  setGaugePercent(percent, duration = 1.2) {
    this.setGaugeState({percent}, duration);
  }

  // The ship in the marker, by its key in content.game.gauge_icons.
  setGaugeIcon(icon, duration = 1.2) {
    this.setGaugeState({icon}, duration);
  }

  // The phase name and the altitude under the marker. Either can be left out.
  setGaugeReadout(label, distance, duration = 1.2) {
    this.setGaugeState({label, distance}, duration);
  }

  // Both gauges back to the top of the descent, with nothing animating.
  resetGauge() {
    if (this.groundDistanceGauge) this.groundDistanceGauge.reset(this.getGaugeStartState());
    if (this.speedGauge) this.speedGauge.reset(this.getSpeedStartState());

    // The dial goes back out of the screen, the align action brings it in again.
    this.showSpeedGauge(false);
  }

  // Dev shortcut: ?startAction=6 opens the run on that action of the JSON
  // sequence, by its id, instead of playing the whole descent to reach it.
  // Returns which action that is in the sequence, or 0 with no parameter and
  // for an id the sequence does not have, so the run starts at the top.
  getStartActionIndex() {
    const id = parseInt(window.getURLParameter("startAction"));
    if (!id) return 0;

    return Math.max(0, this.getSequence().findIndex((config) => config.id === id));
  }

  getSequence() {
    return this.data.content.game.sequence || [];
  }

  // Sets what the skipped actions would have left behind, all at once: the sky
  // partway down, the transit vehicle gone, the ship centered with its shield
  // out, and the gauges where the last skipped action put them. Takes the
  // sequence up to and including the action being opened, since that one sets a
  // background and gauges too. Only used by the dev shortcut above.
  jumpIntoRun(upToTarget) {
    const target = upToTarget[upToTarget.length - 1];
    const state = EDL.JUMP_SHIP_STATE[target.id] || EDL.JUMP_SHIP_STATE.default;

    // The sky is up, at full strength and most of the way down, and it keeps
    // moving from there into the landing.
    // The jump skips the detach, where the idle loop would have faded out.
    this.audio.stop(SOUNDS.idle);

    const sky = EDL.JUMP_SKY;
    this.stopStarTwinkle(0);
    this.fadeOverlayMaskOut(0);
    gsap.set($("#bg-stars"), {autoAlpha: 1});
    gsap.set($("#background-container .bg-lines"), {autoAlpha: 0});
    gsap.set(this.getSkyLayer(), {opacity: 1});
    gsap.set(this.getSky(), {
      x: -1689 * sky.progress,
      y: -3178 * sky.progress
    });
    this.startSkyRise(sky.remaining_sec);
    this.startBackgroundDrift();

    // The transit vehicle is long gone and the capsule has finished drifting
    // back to the middle of the screen.
    this.mtvDetaching = true;
    gsap.set(this.getMTV(), {y: -6000});
    gsap.set(this.getShipPlacement(), {x: this.shipRecenterTravel()});

    // The shield is already out, so it never deploys again, and the ship holds
    // the last frame of whichever animation this jump lands on.
    this.shieldDeploying = true;
    this.shieldDeployed = Promise.resolve(true);
    this.holdSequenceFrame(state.sequence);

    if (state.thrusters) {
      this.lightThrusters([this.thrustersBySide.center], 0);
      this.lightThrusters([this.thrustersBySide.left, this.thrustersBySide.right], 0, EDL.SIDE_THRUSTER_IDLE);
    }

    // The background of the action being opened, and the gauges where the
    // skipped actions left them.
    const stage = upToTarget.map((config) => config.stage).filter(Boolean).pop();
    if (stage) this.setStage(stage, true);

    this.setGaugeState(this.getLastGaugeState(upToTarget), 0);

    // The dial is already up whenever the jump lands on the align action or
    // later, since that is where the run would have brought it in.
    if (target.id >= EDL.SPEED_GAUGE_ACTION_ID) this.showSpeedGauge(true, 0);
  }

  // The last gauge state those actions set: what the transition after the action
  // travelled to, else how the action ended, else its last press, else how it
  // started.
  getLastGaugeState(actions) {
    return actions.reduce((last, config, index) => {
      const presses = config.presses || [];

      // The transition after an action has only played when the jump lands past
      // it, so it never counts for the action being opened.
      const transitionEnd = index < actions.length - 1 ? config.gauge_at_transition_end : null;

      return transitionEnd
        || config.gauge_at_sequence_end
        || (presses.length ? presses[presses.length - 1].gauge : null)
        || config.gauge_at_sequence_start
        || last;
    }, null);
  }

  // Shows one animation on its last frame and hides all the others, so a point
  // in the run is reached without playing it.
  holdSequenceFrame(name) {
    FrameAnimation.stopAll();

    EDL.SEQUENCE_TAKEOVERS.forEach((takeover) => {
      $(`#ship-container [data-name='${takeover.from}'], #ship-container [data-name='${takeover.to}']`)
        .addClass("start-hidden");
    });

    const animation = this.getAnimation(name);
    if (!animation) return;

    $(animation.root).removeClass("start-hidden");
    animation.showFrame(animation.frames.length - 1);
  }

  // Intro timeline revealing the screen 2 elements, then the game starts. The
  // action container comes in empty and stays visible for the whole run, only
  // its content swaps between actions.
  //
  // Before any of that the pre-game note has its moment: the instructions
  // pointing at the empty container, alone on the screen with no gauges and
  // nothing to press. Then it all arrives one piece at a time, the note out,
  // the gauges in, and the first action last.
  playGameIntro() {
    this.killTimeline(this.currentTimeline);

    // The label starts folded so the container reveals without an empty pill,
    // the first action expands it with its text.
    this.game.collapseLabel(true);

    const intro = this.getGameIntro();
    this.game.setInstructions(intro.text, intro.subtext);

    const $instructions = this.game.getInstructionParts();

    this.currentTimeline = gsap.timeline({
      onComplete: () => this.game.start()
    });

    this.currentTimeline
      .fromTo($("#screen-2 .action-container"), {
        autoAlpha: 0,
        y: 40
      }, {
        autoAlpha: 1,
        y: 0,
        duration: 0.5,
        ease: "power2.out"
      })
      .fromTo($instructions, {
        autoAlpha: 0,
        y: 40
      }, {
        autoAlpha: 1,
        y: 0,
        duration: 0.5,
        stagger: 0.1,
        ease: "power2.out"
      }, "<")
      .to({}, {duration: intro.hold_sec || 0})
      .to($instructions, {
        autoAlpha: 0,
        duration: 0.4,
        ease: "power1.inOut"
      })
      .add(this.showGauges(true))

      // A beat on the settled gauges before the first action comes in.
      .to({}, {duration: 0.5});
  }

  // The note shown before the run, and how long it stays up, from the JSON.
  getGameIntro() {
    return this.data.content.game.intro || {hold_sec: 0};
  }

  // Transition gap between two actions: the previous content is already faded
  // out and cleared, the next one launches once the returned promise resolves.
  // This is where the stage swap and any transition timeline run. With nothing
  // to play, resolve right away.
  //
  // The gauges move on their own from the states of the actions in the JSON.
  // Move them by hand here with setGaugePercent, setGaugeIcon, setGaugeReadout
  // or setSpeedState when a transition needs them to travel along with the
  // animation, a shield deploying halfway through being the usual case.
  beforeAction(config, previousConfig) {
    return new Promise((resolve) => {
      if (config.stage !== undefined && !EDL.SELF_STAGED_ACTIONS.includes(config.id)) {
        this.setStage(config.stage);
      }

      // Build a transition timeline here when one is needed and resolve in
      // its onComplete instead. Actions are identified by their id from the
      // JSON sequence, for example:
      // if (config.id === 4) {
      //   gsap.timeline({onComplete: resolve}).to(...);
      //   return;
      // }

      // Whatever the previous transition was still playing, it is over now.
      this.killTimeline(this.currentTransitionTimeline);
      this.currentTransitionTimeline = null;

      // The idle ambience goes quietly as the run reaches the detach.
      if (config.id === 1) this.audio.stop(SOUNDS.idle, 3);

      // The detach happens on its press, but a press that never came still
      // leaves the vehicle behind once the run moves on, so space shows up
      // before the burn either way. Does nothing when the press already did it.
      if (config.id === 2) this.startMTVDetach();

      // From here on the background is bright, so the label needs its shadow to
      // stay readable. Taken off again on reset.
      if (config.id === 3) $("#screen-2 .action-container").addClass("on-background-color");

      switch (config.id) {
        case 3: {
          // The burn action is done, so the burn itself plays out: the capsule
          // sequence first, then the three thrusters fire a short burn while
          // the sky slows down under them. They go out again and the capsule coasts on with its engines
          // off before the deploy action opens.
          // delay_sec waits after the capsule animation before the engines
          // light, light_sec is how long they take to come up, burn_sec how
          // long they burn once lit, cut_sec how long they take to go out and
          // coast_sec how long the capsule drifts on before the next action.
          // background_speed is the pace the sky slows to over the burn.
          const delaySec = this.getTransitionSec(previousConfig, "delay_sec", 0);
          const lightSec = this.getTransitionSec(previousConfig, "light_sec", 0.5);
          const burnSec = this.getTransitionSec(previousConfig, "burn_sec", 2);
          const cutSec = this.getTransitionSec(previousConfig, "cut_sec", 0.8);
          const coastSec = this.getTransitionSec(previousConfig, "coast_sec", 2.5);
          const backgroundSpeed = this.getTransitionSec(
            previousConfig,
            "background_speed",
            EDL.DEORBIT_BURN_BACKGROUND_SPEED
          );

          this.playDeorbitBurn().then(() => {
            // The capsule animation is over, so the wait here is whatever the
            // JSON asks for on top of it before the engines light.
            this.wait(delaySec).then(() => {
              // The gauge of the burn only starts moving once the engines
              // actually light, and it travels right up to the moment the next
              // action opens.
              this.applyTransitionGauge(previousConfig, lightSec + burnSec + cutSec + coastSec);

              this.fireDeorbitBurn(lightSec, burnSec, cutSec, backgroundSpeed)
                .then(() => this.wait(coastSec))
                .then(resolve);
            });
          });
          return;
        }

        case 4: {
          // The beats of this transition, in the order they play, so the gauge
          // below can travel over all of them at once: the sky warming up, the
          // fire building and then holding, the ship flying onto its path, and
          // the pause before the next action opens.
          const skySec = this.getTransitionSec(previousConfig, "sky_sec", 2);
          const glowBuildSec = this.getTransitionSec(previousConfig, "glow_build_sec", 2);
          const glowHoldSec = this.getTransitionSec(previousConfig, "glow_hold_sec", 3);
          const alignSec = this.getTransitionSec(previousConfig, "align_sec", 1.2);
          const settleSec = this.getTransitionSec(previousConfig, "settle_sec", 0.6);

          // The shield deploys the same way whatever the press did, so a press
          // that was wrong or never came still gets it out here. A deploy
          // already under way is simply waited on. Then the sky warms up, and
          // only once it has does the entry itself start.
          this.deployHeatShield()
            .then(() => {
              // The shield is out, which is when the ship starts slowing down in
              // the air, so this is where the gauge sets off. It travels over
              // everything left in the transition and lands as the next action
              // opens. The phase name and the ship icon wait for the fire below.
              this.applyTransitionGauge(
                previousConfig,
                skySec + glowBuildSec + glowHoldSec + alignSec + settleSec,
                ["label", "icon"]
              );

              return this.fadeSkyToAtmosphere(skySec);
            })
            .then(() => {
              // The gauge reads the entry interface until the shield catches
              // fire, and turns to the heat shield as it does.
              this.showHeatShieldOnGauge(previousConfig);

              return this.lightShipHeatGlow(glowBuildSec, glowHoldSec);
            })
            .then(() => {
              // Entering the atmosphere. The ship simply flies onto the entry
              // path, still burning: there is no swap and nothing to cross-fade,
              // so the fire never dips. The path it is flown against and the
              // target it is held in come up around it as it arrives.
              this.setStage(config.stage);
              this.showAlignFrame(true);

              // Speed is what this action is flown on, so the dial rises into
              // place as the ship arrives on its path.
              this.showSpeedGauge(true);

              return this.alignShipWithEntryArt(alignSec);
            })
            .then(() => {
              this.currentTransitionTimeline = gsap.timeline({onComplete: resolve});
              this.currentTransitionTimeline.to({}, {duration: settleSec});
            });
          return;
        }

        case 5:
          // The align action slides the ship back to the middle of the screen as
          // it ends, so the descent waits for it to arrive rather than growing a
          // ship that is still sliding. The path and the target go with the run
          // that was flown against them, and so does the fire: the entry is over
          // and the ship is through the worst of the air.
          this.showAlignFrame(false);
          this.shipHeatGlow.buildExit(1.5);
          this.audio.stop(SOUNDS.fire, 1.5);

          this.waitForShipToSettle().then(() => {
            this.setStage(config.stage);

            // The entry was flown on a capsule shrunk onto its path. The descent
            // grows it back to the size it was drawn at for the burn, which is
            // what the rest of the run plays at.
            this.growDescentShip();

            // The powered descent loses altitude from the moment it opens.
            this.startPoweredDescentGauge(config);
            resolve();
          });
          return;

        case 6: {
          // The align run is over and the ship is back at full size, so it opens
          // up: doors, engines, then legs. The stabilize action only starts once
          // it is standing on them, engines still burning. The ground is used
          // next, so it is decoded here while the doors play.
          this.decodeMarsGround();

          // legs_delay_sec is how long the engines burn alone before the legs
          // come down over them.
          const legsDelaySec = this.getTransitionSec(previousConfig, "legs_delay_sec", 2);

          // The altitude has been coming down since the action opened, see
          // startPoweredDescentGauge. It takes whatever it has left over the
          // whole transition from here, so it lands as the stabilize action
          // opens however quickly the player got through the sequence.
          this.applyTransitionGauge(previousConfig, this.getDescentTransitionSec(previousConfig), ["speed"]);

          // The speed is what the engines take off, so it sets off when they
          // light and travels over what is left, the wait and the legs coming
          // down.
          this.runDescentSequence(legsDelaySec, (remainingSec) => {
            const end = this.getTransitionGaugeState(previousConfig);
            if (end) this.setSpeedState(end.speed, remainingSec);
          }).then(resolve);
          return;
        }

        case 7: {
          // The ship is on its legs with the engines burning, so the last of
          // the fall plays out: the ground comes up, everything moving in the
          // sky slows to a stop, and the engines go out. The last action starts
          // once it has landed.
          const landingSec = this.getTransitionSec(previousConfig, "landing_sec", EDL.LANDING_SEC);

          // The altitude and the speed have been coming down since the
          // stabilize run ended. They take whatever they have left over the
          // whole landing from here, so both read zero exactly as the ship
          // touches down, easing into it with the ground. The phase name and
          // the landed ship wait for the touchdown.
          this.applyTransitionGauge(previousConfig, this.getLandingSec(previousConfig), ["label", "icon"]);

          this.landOnMars(landingSec).then(() => {
            this.showLandedOnGauge(previousConfig);
            resolve();
          });
          return;
        }
      }

      resolve();
    });
  }

  // What a press sets off on the ship itself, next to what the action does with
  // it. The detach starts on the press, not once the action is over.
  onGameButtonPress(gameAction) {
    if (gameAction.config.id === 1) this.startMTVDetach();
    if (gameAction.config.id === 3) this.deployHeatShield();
  }

  // The gameplay of an action is over and its verdict is showing, before the
  // hold and the swap to the next one.
  //
  // The stabilize run ends at 65 ft, which is where the landing starts: the
  // altitude and the speed carry on to zero instead of waiting at 65 ft
  // through the hold, the swap and the shrink. They go over about as long as
  // all that and the fall take, and the landing takes over whatever is left
  // once it starts, see beforeAction. The engines start throttling back on the
  // same beat, see startLandingThrottle.
  onGameActionFinished(gameAction) {
    if (gameAction.config.id !== 6) return;

    this.startLandingThrottle(gameAction.config);

    this.applyTransitionGauge(
      gameAction.config,
      this.getBeforeLandingSec() + this.getLandingSec(gameAction.config),
      ["label", "icon"],
      "none"
    );
  }

  // The engines handed over to the landing as the stabilize run ends at 65 ft.
  // All three first settle on the same throttle, the middle one coming down
  // and the outer ones coming up from their idle. From there they come down
  // together at a steady pace all the way to nothing, over about as long as
  // what is left before the ground: the rest of the hold, the swap and the
  // whole landing. The fall takes over whatever is left once it starts, see
  // dropOntoMars.
  startLandingThrottle(stabilizeConfig) {
    const settleSec = EDL.LANDING_THROTTLE_SETTLE_SEC;
    const remainingSec = this.getBeforeLandingSec() + this.getLandingSec(stabilizeConfig) - settleSec;

    this.thrusters
      .filter((thruster) => thruster.running)
      .forEach((thruster) => thruster.start(settleSec, EDL.LANDING_THRUST));

    // The roar goes down with them, over the whole way to the ground, so it
    // is gone as the legs touch. The fall asking for the same fade later
    // leaves this one running, see windDownThrusters.
    this.audio.stop(SOUNDS.thrusters, settleSec + remainingSec);

    this.wait(settleSec).then(() => {
      this.throttleThrusters(0, remainingSec, "none");
    });
  }

  // From the end of the stabilize run to the landing starting: the verdict
  // hold, the pause and the swap of the action content.
  getBeforeLandingSec() {
    const game = this.game;

    return game.stateHoldSec + game.betweenActionsSec + game.swapFadeSec;
  }

  // The whole landing: the ship shrinking to its landing size, then the fall.
  getLandingSec(stabilizeConfig) {
    return EDL.LANDING_SHRINK_SEC + this.getTransitionSec(stabilizeConfig, "landing_sec", EDL.LANDING_SEC);
  }

  // The transit vehicle leaves once the capsule has detached from it. It eases
  // off, picks up speed and climbs straight off the top of the screen, gone for
  // good before the burn. Moving it on its own y takes care of the tilt, it
  // lives inside the tilted assembly, so up in there is up along the ship axis.
  // The capsule itself never moves.
  startMTVDetach() {
    if (this.mtvDetaching) return;
    this.mtvDetaching = true;

    // The capsule starts drifting back to the middle of the screen on the same
    // press, and space shows up around it, already rushing past.
    this.startShipRecenter();
    this.revealSpace();

    gsap.to(this.getMTV(), {
      y: -6000,
      duration: EDL.MTV_EXIT_SEC,
      ease: "power1.in",
      overwrite: "auto"
    });
  }

  // The stars and the arcs, hidden on the idle screen, fade in at the detach.
  // They are already travelling at the orbit pace underneath the fade, so the
  // sky shows up rushing past rather than getting going.
  revealSpace(duration = EDL.SPACE_REVEAL_SEC) {
    this.backgroundSpeed = EDL.DETACH_BACKGROUND_SPEED;
    this.startBackgroundDrift();

    gsap.to($("#bg-stars, #background-container .bg-lines"), {
      autoAlpha: 1,
      duration: duration,
      ease: "power1.inOut"
    });
  }

  // The sky starts travelling and keeps travelling for the rest of the run, so
  // nothing waits on this. The arcs climb much slower than the stars, and both
  // wrap on a whole step of their own pattern, which is what makes the endless
  // repeat seamless.
  startBackgroundDrift() {
    // The sky is moving now, so the stars settle at full strength instead of
    // going on blinking.
    this.stopStarTwinkle();

    this.backgroundDrift = [
      gsap.to("#bg-stars", {
        backgroundPosition: "-4622px -8217px",
        duration: 55,
        ease: "none",
        repeat: -1,
        overwrite: "auto"
      }),

      // One spacing step of the arcs, so the group lands on the next arc's place
      // and starts over with nothing to see. The step has to match the spacing of
      // the macro that lays them out, and it stays vertical for the reason
      // written up there.
      gsap.to("#background-container .bg-lines", {
        y: -1350,
        duration: 35,
        ease: "none",
        repeat: -1,
        overwrite: "auto"
      })
    ];

    // Straight at whatever pace the run is at, the fade in is what hides the
    // start.
    this.backgroundDrift.forEach((drift) => drift.timeScale(this.getBackgroundTimeScale()));
  }

  // The background coming to a full stop over the landing, the one place it is
  // allowed below the minimum. The sky travel is not in here: finishSkyRise has
  // already taken it over so it lands on its own timing.
  stopBackgroundDrift(duration = EDL.LANDING_SEC) {
    this.setBackgroundSpeed(0, duration, "power2.out");
  }

  // The patches laid over the star field, one timeline each.
  getStarPatches() {
    return $("#bg-stars-twinkle .bg-stars-patch");
  }

  // The twinkle of the standing sky: every patch starts its own chain, held in
  // its own slot so the drift can put an end to all of them. Restarted by
  // resetState, since a new run starts docked again.
  startStarTwinkle() {
    this.stopStarTwinkle(0);

    // Every patch starts at the bottom of its band, so the sky is already
    // uneven before the first blink instead of coming in from nothing.
    gsap.set(this.getStarPatches(), {opacity: EDL.STAR_TWINKLE.opacity.min});

    this.starTwinkles = [];
    this.getStarPatches().each((index, el) => this.twinklePatch(el, index));
  }

  // One blink of one patch, which books that patch's next blink on its way out.
  // Chained rather than repeated so each pass draws its own numbers instead of
  // replaying the same ones over and over, which is also what keeps the eight of
  // them from ever settling into step: each one waits a different while.
  twinklePatch(el, index) {
    if (!this.starTwinkles) return;

    const twinkle = EDL.STAR_TWINKLE;
    const random = gsap.utils.random;

    this.starTwinkles[index] = gsap.timeline({onComplete: () => this.twinklePatch(el, index)})
      // The wait comes first, so the patches are already scattered in time on
      // the very first pass instead of all starting together at boot.
      .to({}, {duration: random(twinkle.pause.min, twinkle.pause.max)})
      .to(el, {
        opacity: twinkle.opacity.max,
        duration: random(twinkle.duration.min, twinkle.duration.max),
        ease: "sine.inOut"
      })
      .to(el, {
        opacity: twinkle.opacity.min,
        duration: random(twinkle.duration.min, twinkle.duration.max),
        ease: "sine.inOut"
      });
  }

  // Wherever the blinks were caught, back up to a steady sky: every patch all
  // the way out of the way, so the whole field is showing again.
  stopStarTwinkle(duration = 0.6) {
    if (!this.starTwinkles) return;

    this.starTwinkles.forEach((timeline) => timeline.kill());
    this.starTwinkles = null;

    const $patches = this.getStarPatches();
    gsap.killTweensOf($patches);

    // A zero length tween would only land on the next tick, which is too late
    // for a restart setting the patches itself right after this.
    if (duration <= 0) {
      gsap.set($patches, {opacity: 0});
      return;
    }

    gsap.to($patches, {
      opacity: 0,
      duration: duration,
      ease: "sine.out"
    });
  }

  // Back to a capsule docked to its vehicle, thrusters out and shield stowed.
  // With one ship for the whole run this is the only place that undoes what a
  // run did to it, so it has to take every box back, not just the art.
  resetShipAssembly() {
    this.mtvDetaching = false;
    this.shieldDeploying = false;
    this.shieldDeployed = null;

    this.thrusters.forEach((thruster) => thruster.reset());

    // Only the first sequence shows, the three that take the frame over from it
    // are back to waiting their turn.
    FrameAnimation.stopAll();
    EDL.SEQUENCE_TAKEOVERS.forEach((takeover, i) => {
      if (i === 0) $(`#ship-container [data-name='${takeover.from}']`).removeClass("start-hidden");
      $(`#ship-container [data-name='${takeover.to}']`).addClass("start-hidden");
    });

    // The path and the target of the align run go back down without animating.
    gsap.killTweensOf($("#ship-container .ship-trajectory-lines, #ship-container .align-ship-circle"));
    gsap.set($("#ship-container .ship-trajectory-lines, #ship-container .align-ship-circle"), {
      clearProps: "opacity,visibility"
    });
  }

  // The capsule thrusters, played through once and held on the last frame.
  // The pace comes from the fps of the template. Their doors open over it,
  // before the engines fire, the same as in the powered descent.
  playDeorbitBurn() {
    const animation = this.getAnimation("deorbit-burn");
    if (!animation) return Promise.resolve(false);

    this.audio.play(SOUNDS.thrusterDoors, this.getSoundVolume("ship"), true);
    return animation.play();
  }

  // The black of space warming up to the orange of the atmosphere: the sky fades
  // in over the stars while the capsule is still flying its deorbit burn, and
  // stays up from here to the ground.
  fadeSkyToAtmosphere(duration = 2) {
    this.startSkyRise();

    // The dark band at the top belongs to the black of space, so it leaves on
    // the same beat as the sky it was darkening. So do the flight path arcs,
    // which are drawn for space and would otherwise sit on the orange until the
    // entry stage takes them away several seconds later.
    this.fadeOverlayMaskOut(duration);

    gsap.to($("#background-container .bg-lines"), {
      autoAlpha: 0,
      duration: duration,
      ease: "power1.inOut"
    });

    // In quickly, to the haze the atmosphere starts as. This is what the run
    // waits on before carrying on with the entry.
    const fadeIn = gsap.to(this.getSkyLayer(), {
      opacity: EDL.SKY_HAZE,
      duration: duration,
      ease: "power1.inOut"
    });

    // From there it goes on thickening by itself for the rest of the descent, on
    // the same ride as the travel. That is what keeps the sky out of the stages
    // entirely: it never steps at a boundary and is never cross-faded against a
    // copy of itself, it is simply the same layer all the way to the ground.
    gsap.to(this.getSkyLayer(), {
      opacity: 1,
      duration: EDL.SKY_RIDE_SEC,
      delay: duration,
      ease: "none"
    });

    return fadeIn;
  }

  // The phase name and the ship icon the deploy action ends on in the JSON,
  // swapped on their own as the fire lights. The rest of that state travels
  // over the whole transition, see beforeAction.
  showHeatShieldOnGauge(deployConfig) {
    const state = this.getTransitionGaugeState(deployConfig);
    if (!state) return;

    this.setGaugeState({
      label: state.label,
      icon: state.icon
    });
  }

  // Same for the landing: the gauge only reads ground ops, with the landed
  // ship, once the legs are on the ground.
  showLandedOnGauge(stabilizeConfig) {
    const state = this.getTransitionGaugeState(stabilizeConfig);
    if (!state) return;

    this.setGaugeState({
      label: state.label,
      icon: state.icon
    });
  }

  // The air catching fire around the shield the capsule just deployed. It builds
  // over a couple of seconds and then holds, so the shield and its burn are what
  // the screen shows for a beat before the entry itself starts. The same fire
  // goes on burning through the entry, it is never rebuilt.
  lightShipHeatGlow(buildSec = 2, holdSec = 3) {
    this.shipHeatGlow.buildEnter(buildSec);
    this.audio.playLoop(SOUNDS.fire, this.getSoundVolume("fire"), buildSec);

    return this.wait(buildSec + holdSec);
  }

  // Flies the capsule onto the entry path: into the middle of the align target,
  // down to the size it is drawn at there and around to the angle it comes in
  // on. This is the whole transition into the entry, there is nothing to fade.
  //
  // Measured rather than assumed: the capsule has been drifting back to center
  // since the detach, so where it starts from is only known at this point.
  alignShipWithEntryArt(duration = 1.2) {
    const $ship = this.getShipPlacement();
    const ship = $ship.get(0);
    if (!ship) return Promise.resolve();

    const entry = EDL.ENTRY_CAPSULE;
    const box = ship.getBoundingClientRect();

    // Rotation keeps a box centered on its own middle, so this holds while the
    // assembly is turned.
    const x = gsap.getProperty(ship, "x") + entry.centerX - (box.left + box.width / 2);
    const y = gsap.getProperty(ship, "y") + entry.centerY - (box.top + box.height / 2);

    // The turn belongs to the assembly, the rest to the placement box around it,
    // the same split they are laid out with.
    gsap.to(this.getShipAssembly(), {
      rotation: entry.rotation,
      duration: duration,
      ease: "power2.inOut",
      overwrite: "auto"
    });

    return gsap.to($ship, {
      x: x,
      y: y,
      scale: this.getEntryCapsuleScale(),
      duration: duration,
      ease: "power2.inOut",
      overwrite: "auto"
    });
  }

  // The scale that makes the capsule the size it is during the entry. The
  // placement box is laid out at the bigger size used for the burn, so this is
  // one size divided by the other. The landing uses it too.
  getEntryCapsuleScale() {
    return EDL.ENTRY_CAPSULE.frameWidth / this.getShipAssembly().width();
  }

  // The three boxes of the one ship, see the template for what each of them is
  // allowed to move. Queried fresh rather than cached, the page is re-rendered
  // by nunjucks after this class is built.
  getShip() {
    return $("#ship-container .ship");
  }

  getShipPlacement() {
    return $("#ship-container .ship-placement");
  }

  getShipAssembly() {
    return $("#ship-container .ship-assembly");
  }

  // The path and the target of the align run, raised together for it and taken
  // back down after. Everything else about the ship simply carries on.
  showAlignFrame(shown, duration = 1) {
    return gsap.to($("#ship-container .ship-trajectory-lines, #ship-container .align-ship-circle"), {
      autoAlpha: shown ? 1 : 0,
      duration: duration,
      ease: "power1.inOut",
      overwrite: "auto"
    });
  }

  // Takes the capsule from the size the entry is flown at back to the one it had
  // before alignShipWithEntryArt shrank it. The placement box is laid out at
  // that size, so back to it is simply scale 1. Scale only, it grows where it
  // stands, so the centering the align run ended on is kept.
  // The other way round, before the landing: back down to the size used for the
  // entry, which is the size the ship lands at. Scale only, like the growth, so
  // it shrinks where it is.
  shrinkLandingShip(duration = EDL.LANDING_SHRINK_SEC) {
    return gsap.to(this.getShipPlacement(), {
      scale: this.getEntryCapsuleScale(),
      duration: duration,
      ease: "power2.inOut",
      overwrite: "auto"
    });
  }

  growDescentShip(duration = 1.2) {
    const $ship = this.getShipPlacement();
    if (!$ship.length) return Promise.resolve();

    return gsap.to($ship, {
      scale: 1,
      duration: duration,
      ease: "power2.inOut",
      overwrite: "auto"
    });
  }

  // The band darkening the top of the screen, taken off with the black sky it
  // belongs to and gone for the rest of the run. Put back by resetState.
  fadeOverlayMaskOut(duration = 1) {
    gsap.to(this.getOverlayMask(), {
      autoAlpha: 0,
      duration: duration,
      ease: "power1.inOut",
      overwrite: "auto"
    });
  }

  getOverlayMask() {
    return $("#overlay-mask-container");
  }

  // The sky travelling from its dark top left corner to the bright bottom right
  // one, which is the ground coming up under a falling ship. It starts with the
  // atmosphere fading in and lands with the ship, and since the run is as long
  // as the player makes it, this only has to be slow enough not to arrive early:
  // the landing closes whatever is left, see finishSkyRise.
  startSkyRise(duration = EDL.SKY_RIDE_SEC) {
    // Kept, because it is played at the speed of the run like the drifts are.
    this.skyRise = gsap.to(this.getSky(), {
      x: -1689,
      y: -3178,
      duration: duration,
      ease: "none",
      overwrite: "auto"
    });

    // It joins a background already moving at the speed of the run.
    this.skyRise.timeScale(this.getBackgroundTimeScale());
  }

  // Whatever the rise has left to go, closed over the landing itself, so the
  // sky reaches its bottom right corner as the ship touches down. It only eases
  // out, since it has been moving at a steady pace all descent and this is it
  // stopping, not starting again.
  finishSkyRise(duration = EDL.LANDING_SEC) {
    // From here the sky is on the timing of the landing, not on the speed of
    // the run, so the pace of the background stops driving it.
    this.skyRise = null;

    gsap.to(this.getSky(), {
      x: -1689,
      y: -3178,
      duration: duration,
      ease: "power2.out",
      overwrite: "auto"
    });

    gsap.to(this.getSkyLayer(), {
      opacity: 1,
      duration: duration,
      ease: "power1.out",
      overwrite: "auto"
    });
  }

  // The landing. The ground comes up from the bottom right and everything else
  // still moving slows to a stop with it, which reads as the ship slowing down
  // near the surface. It leans once and straightens before the legs touch, and
  // the engines fade out on the way down.
  // onDrop is called the moment the fall itself starts, once the ship is down to
  // its landing size, so a gauge can travel over exactly that stretch.
  landOnMars(duration = EDL.LANDING_SEC, onDrop = null) {
    // The descent grew the capsule back to the size it burns at, which is much
    // too big to stand on a horizon. It lands at the smaller entry size, so it
    // shrinks back down before the ground shows up.
    return this.shrinkLandingShip().then(() => {
      if (onDrop) onDrop();

      return this.dropOntoMars(duration);
    });
  }

  // The fall itself, once the ship is down to its landing size. Everything runs
  // on the same stretch: the ground comes up, the sky slows to a stop, the ship
  // leans once and straightens, and the engines wind down to nothing so they
  // are off by the time it is standing.
  dropOntoMars(duration = EDL.LANDING_SEC) {
    this.finishSkyRise(duration);
    this.stopBackgroundDrift(duration);
    this.windDownThrusters(duration);

    const $ground = this.getMarsGround();
    const entry = EDL.GROUND_ENTRY;

    // Moved off screen before it is shown, so the first frame of it is already
    // in the corner it comes in from.
    gsap.set($ground, {
      x: entry.x,
      y: entry.y,
      autoAlpha: 1
    });

    const $assembly = this.getShipAssembly();
    const $placement = this.getShipPlacement();
    const $shadow = this.getMarsGroundShadow();
    const shadow = EDL.LANDING_SHADOW;
    const dust = this.dustCloud;

    // The shadow is a spot on the surface, not a spot on the screen: it belongs
    // to the ground the legs come down on, and that ground is still travelling.
    // So it starts from the same corner and rides the same journey, and its own
    // growth below is played on top of that.
    gsap.set($shadow, {
      x: entry.x,
      y: entry.y
    });

    return new Promise((resolve) => {
      this.currentTransitionTimeline = gsap.timeline({onComplete: resolve});

      this.currentTransitionTimeline
        // The landing itself, everything else just dresses it up. It ends with
        // the bottom of the art on the bottom of the screen.
        .to($ground, {
          x: 0,
          y: 0,
          duration: duration,
          ease: "power1.out"
        }, 0)

        // The last of the fall, slowing down at the same time. Much shorter
        // than the ground's travel, so the ship looks like it is holding on its
        // engines while the surface comes up.
        .to($placement, {
          y: `+=${EDL.LANDING_DROP}`,
          duration: duration,
          ease: "power1.out"
        }, 0)

        // Carried along by the surface it lies on, the same travel as the art
        // above, so it arrives on the landing spot with it.
        .to($shadow, {
          x: 0,
          y: 0,
          duration: duration,
          ease: "power1.out"
        }, 0)

        // And closing in under the ship as that happens. It starts small and
        // faint partway through the fall and is whole the moment the legs touch,
        // which is what reads as the ship closing the last of the distance.
        .fromTo($shadow, {
          autoAlpha: 0,
          scale: shadow.scaleFrom
        }, {
          autoAlpha: 1,
          scale: 1,
          duration: duration * (1 - shadow.startAt),
          ease: "power1.in"
        }, duration * shadow.startAt)

        // The dust the engines blow off the surface once it is close enough,
        // building to its thickest just before the touchdown and no longer
        // blown once the engines are out. In the timeline so it holds with
        // the rest of the landing.
        .to(dust, {
          strength: 1,
          duration: duration * (EDL.LANDING_DUST.peakAt - EDL.LANDING_DUST.startAt),
          ease: "power1.in",
          onStart: () => dust.startAmbient()
        }, duration * EDL.LANDING_DUST.startAt)
        .to(dust, {
          strength: 0,
          duration: duration * (1 - EDL.LANDING_DUST.peakAt),
          ease: "power1.out"
        }, duration * EDL.LANDING_DUST.peakAt)

        // One lean to the left and back level, both finished before the legs
        // touch, so the ship is not turning when it lands.
        .to($assembly, {
          rotation: -7,
          duration: duration * 0.45,
          ease: "sine.inOut"
        }, 0)
        .to($assembly, {
          rotation: 0,
          duration: duration * 0.4,
          ease: "sine.inOut"
        }, duration * 0.45);
    });
  }

  // The two big images nothing shows until the end of a run: the ground and the
  // conclusion background. Neither is drawn before then, so the browser only
  // decodes them when they first appear, and decoding an image that size blocks
  // everything for a moment. The ground would pop in already halfway up the
  // screen instead of rising into view, and the conclusion would appear instead
  // of fading in. So they are decoded here at boot, and kept so the browser does
  // not throw them away.
  preloadHeavyArt() {
    this.decodeMarsGround();

    this.preloadedArt = ["images/edl/background-endingscreen-2.jpg"].map((url) => {
      const image = new Image();
      image.src = url;
      if (image.decode) image.decode().catch(() => {
      });

      return image;
    });
  }

  // The ground on its own. The animations played during a run are big enough to
  // push it out of what the browser keeps decoded, so the descent asks for it
  // again a few seconds before the landing.
  decodeMarsGround() {
    const ground = this.getMarsGround().get(0);
    if (ground && ground.decode) ground.decode().catch(() => {
    });
  }

  // The ground of Mars, one image for the landing and everything after it. It
  // sits outside the stages because it moves into place instead of fading in.
  getMarsGround() {
    return $("#background-container .mars-ground");
  }

  getMarsGroundShadow() {
    return $("#background-container .mars-ground-shadow");
  }

  // The sky layer itself, whose opacity is the stage ramp, and the art inside it,
  // which is what travels. Kept apart so the two never fight over the same tween.
  getSkyLayer() {
    return $("#background-container .colored-background");
  }

  getSky() {
    return $("#background-container .colored-background svg");
  }

  // The three thrusters under the heat shield, one burn per pod of the art. Same
  // engine as the ship of Path to Mars, sized off its own canvas: the nozzle
  // sits a fifth of the way down and the flame runs down the rest of the box,
  // which ship.scss places on the pod. The room above the nozzle keeps the core
  // glow from being cut flat by the canvas edge, with spare for a bigger glow.
  // One set for the whole run: the same three pods fire for the deorbit burn, go
  // out for the entry, and come back once the descent opens their doors.
  initThrusters() {
    this.thrusters = $("#ship-container .ship-thruster")
      .map((i, canvas) => new ThrusterGlow($(canvas), {
        nozzleX: 0.5,
        nozzleY: 0.207,
        // Denser than the single engine of the flight to Mars: three of these
        // fire at once and they are read from much closer. They also reach much
        // further down the canvas and their puffs live longer, which is what
        // makes a beam out of what is otherwise a flame.
        emitPerSecond: 270,
        thrusterLength: 0.56,
        lifeMin: 0.33,
        lifeMax: 1.42,

        // A wide soft glow sitting on the nozzle, so the three beams come out of
        // something rather than starting in mid air.
        coreSize: 0.8,
        coreAlpha: 0.49,
        palette: EDL.THRUSTER_PALETTE,

        // The descent burns read as beams rather than flames: wide where they
        // leave the nozzle and down to nothing at the far end, which is the
        // triangle of the design.
        sizeStart: 0.38,
        sizeEnd: 0,
        spread: 0.055
      }))
      .get();

    // Keyed by the pod they sit on, for the runs that drive one side on its own.
    this.thrustersBySide = {};
    $("#ship-container .ship-thruster").each((i, canvas) => {
      const side = ["left", "center", "right"].find((name) => canvas.classList.contains(name));
      if (side) this.thrustersBySide[side] = this.thrusters[i];
    });
  }

  getSideThrusterIdleLevel() {
    return EDL.SIDE_THRUSTER_IDLE;
  }

  // The two outer engines at their own throttle, 1 being a full burn. The
  // stabilize action runs them low and pushes one back up to show which way the
  // ship is being steered.
  setSideThrusterLevels(leftLevel, rightLevel, duration = EDL.SIDE_THRUSTER_RAMP_SEC) {
    if (this.thrustersBySide.left) this.thrustersBySide.left.start(duration, leftLevel);
    if (this.thrustersBySide.right) this.thrustersBySide.right.start(duration, rightLevel);
  }

  lightThrusters(thrusters, duration = 0.8, level = 1) {
    thrusters.filter(Boolean).forEach((thruster) => thruster.start(duration, level));

    // One loop for all the engines, already playing when a second set lights.
    this.audio.playLoop(SOUNDS.thrusters, this.getSoundVolume("thrusters"), duration);

    return this.wait(duration);
  }

  // The engines that are burning pulled back to a throttle, over the ramp the
  // side engines use unless told otherwise, so the flames are seen shrinking.
  // Only ever down: an engine already burning lower than that is left where it
  // is rather than flaring up to meet the others.
  throttleThrusters(level, duration = EDL.SIDE_THRUSTER_RAMP_SEC, ease) {
    this.thrusters
      .filter((thruster) => thruster.running && thruster.intensity > level)
      .forEach((thruster) => thruster.start(duration, level, ease));
  }

  // The last of the landing throttle: whatever the engines still burn goes to
  // nothing at the same steady pace over the fall, so the flames die exactly
  // as the legs touch, and they are put away once they have. They have been
  // winding down since the end of the stabilize run, see startLandingThrottle.
  windDownThrusters(duration) {
    this.throttleThrusters(0, duration, "none");

    // Already fading since the stabilize run ended, in which case this does
    // nothing. Only a run opened straight on the landing starts it here.
    this.audio.stop(SOUNDS.thrusters, duration);

    // Even at the bottom of the throttle an engine still shows a small flame,
    // and it only goes out at exactly nothing, which would pop. So the canvas
    // fades over the last of the fall and the flame dies with it.
    this.thrusters.forEach((thruster) => {
      gsap.to(thruster.$canvas, {
        autoAlpha: 0,
        duration: duration * 0.35,
        delay: duration * 0.65,
        ease: "power1.in"
      });
    });

    return this.wait(duration).then(() => {
      this.thrusters.forEach((thruster) => thruster.reset());
    });
  }

  cutThrusters(thrusters, duration = 1) {
    thrusters.forEach((thruster) => thruster.stop(duration));
    this.audio.stop(SOUNDS.thrusters, duration);

    return this.wait(duration);
  }

  // However long the align run still has the ship travelling back to the middle
  // of the screen, so the descent can wait it out rather than starting to grow a
  // ship that is still sliding. Nothing moving, no wait.
  waitForShipToSettle() {
    const steered = this.getShip().get(0);
    if (!steered) return Promise.resolve();

    const remaining = gsap.getTweensOf(steered)
      .reduce((longest, tween) => Math.max(longest, tween.duration() - tween.time()), 0);

    return this.wait(remaining);
  }

  // A pause the run can wait on, held with everything else when the "still
  // there?" modal freezes the gameplay.
  wait(seconds) {
    return new Promise((resolve) => gsap.delayedCall(seconds, resolve));
  }

  // The deorbit burn: the engines light, burn for burnSec once they are up, and
  // go out over cutSec. The promise resolves once they are out.
  //
  // The burn is what slows the capsule down, so the sky loses speed at the rate
  // the engines push: gently while they light, steadily at full burn, and
  // settling as they go out. Each ramp counts for about half its length at full
  // thrust, which is how the drop is shared between the three. The ramps are
  // eased so they meet the steady middle at the same rate, with no kink.
  fireDeorbitBurn(lightSec = 0.5, burnSec = 2, cutSec = 0.8, backgroundSpeed = EDL.DEORBIT_BURN_BACKGROUND_SPEED) {
    const from = this.backgroundSpeed;
    const thrustSec = lightSec / 2 + burnSec + cutSec / 2;
    const speedAfter = (sec) => from + (backgroundSpeed - from) * (thrustSec ? sec / thrustSec : 1);

    this.lightThrusters(this.thrusters, lightSec);
    this.setBackgroundSpeed(speedAfter(lightSec / 2), lightSec, "power1.in");

    return this.wait(lightSec)
      .then(() => {
        this.setBackgroundSpeed(speedAfter(lightSec / 2 + burnSec), burnSec, "none");
        return this.wait(burnSec);
      })
      .then(() => {
        this.setBackgroundSpeed(backgroundSpeed, cutSec, "power1.out");
        return this.cutThrusters(this.thrusters, cutSec);
      });
  }

  // The deploy: the shield inflates on a capsule that has been coasting with
  // its engines off since the burn. Runs once per run, on the press that asked
  // for it or, when that press was wrong or never came, on the way to the next
  // action. Returns the same promise either way, so the caller can wait for the
  // shield to be out.
  deployHeatShield() {
    if (this.shieldDeploying) return this.shieldDeployed || Promise.resolve(false);
    this.shieldDeploying = true;

    this.finishShipRecenter();
    this.shieldDeployed = this.playDeployHeatShield();

    return this.shieldDeployed;
  }

  // Hands the frame from one sequence of a stage to the next: the one taking
  // over is shown, the one it continues is hidden under it, and it plays.
  playTakeover(name) {
    const takeover = EDL.SEQUENCE_TAKEOVERS.find((entry) => entry.to === name);
    const next = this.getAnimation(name);
    if (!next) return Promise.resolve(false);

    $(next.root).removeClass("start-hidden");

    const previous = this.getAnimation(takeover.from);
    if (previous) $(previous.root).addClass("start-hidden");

    return next.play();
  }

  playDeployHeatShield() {
    this.audio.play(SOUNDS.heatShield, this.getSoundVolume("ship"), true);
    return this.playTakeover("deploy-heat-shield");
  }

  // The powered descent loses altitude for the whole of its action and the
  // opening up after it, not only once the sequence is done. The altitude and
  // the fill set off as the action opens, towards where the action ends in the
  // JSON, over about as long as it can take: the whole memorize phase, the
  // whole execute time and the transition after it. A player done early is
  // caught up by the transition, which takes whatever is left over its own
  // length. The speed is left to the engines, see beforeAction.
  startPoweredDescentGauge(config) {
    const end = this.getTransitionGaugeState(config);
    if (!end) return;

    const showSec = config.show_duration !== undefined ? config.show_duration : 2;
    const executeSec = config.execute_duration !== undefined ? config.execute_duration : 10;
    const memorizeSec = (config.sequence || []).length * showSec;

    this.setGaugeState({
      percent: end.percent,
      distance: end.distance,
      unit: end.unit,
      ease: end.ease
    }, memorizeSec + executeSec + this.getDescentTransitionSec(config));
  }

  // How long the opening up after the powered descent lasts: the doors, the
  // engines burning alone, and the legs coming down.
  getDescentTransitionSec(config) {
    return this.getAnimationSec("descent-thruster-doors")
      + this.getTransitionSec(config, "legs_delay_sec", 2)
      + this.getAnimationSec("descent-landing-legs");
  }

  // The descent: the doors of the three engines open, they light, and the legs
  // come down over them a couple of seconds later. Everything stays lit for the
  // stabilize action that flies this same ship next.
  //
  // onEnginesLit is called the moment they light, with how many seconds are
  // left to play, so a gauge can travel over exactly that stretch.
  runDescentSequence(legsDelaySec = 2, onEnginesLit = null) {
    this.audio.play(SOUNDS.thrusterDoors, this.getSoundVolume("ship"), true);

    return this.playTakeover("descent-thruster-doors")
      .then(() => {
        // The middle engine comes up full, the two outer ones straight to the
        // idle the stabilize action steers from.
        this.lightThrusters([this.thrustersBySide.center]);
        this.lightThrusters([this.thrustersBySide.left, this.thrustersBySide.right], 0.8, EDL.SIDE_THRUSTER_IDLE);

        if (onEnginesLit) {
          onEnginesLit(legsDelaySec + this.getAnimationSec("descent-landing-legs"));
        }

        return this.wait(legsDelaySec);
      })
      .then(() => {
        this.audio.play(SOUNDS.landingLegs, this.getSoundVolume("ship"), true);
        return this.playTakeover("descent-landing-legs");
      });
  }

  // The two angles the stabilize action works between, both on the assembly,
  // which is the box that carries the turn of the ship. Level is simply zero:
  // the art is drawn upright and every tilt of the run is a rotation away from
  // it. The ship comes out of the entry at the angle it was flown in on and
  // keeps it until the player turns it back.
  getShipLevelRotation() {
    return 0;
  }

  getShipEntryTilt() {
    return EDL.ENTRY_CAPSULE.rotation;
  }

  // The capsule drifts back toward the middle of the screen once it is on its
  // own. It sits off center while docked to make room for the vehicle above it,
  // and there is nothing to make room for any more. Slow enough to go unnoticed
  // next to everything else moving, and the deploy closes whatever is left of
  // it, see finishShipRecenter.
  startShipRecenter(duration = 30) {
    gsap.to(this.getShipPlacement(), {
      x: this.shipRecenterTravel(),
      duration: duration,
      ease: "power1.inOut",
      overwrite: "auto"
    });
  }

  // Whatever the drift has left to go, closed over a couple of seconds, so the
  // capsule is always centered by the time its shield is out however long the
  // player took to get there.
  finishShipRecenter(duration = 2) {
    gsap.to(this.getShipPlacement(), {
      x: this.shipRecenterTravel(),
      duration: duration,
      ease: "power2.out",
      overwrite: "auto"
    });
  }

  // How far left the capsule travels: from the 52.6% of the screen it is docked
  // at to the 48.6% that centers it, in pixels of that screen. Keep it in step
  // with the left of the ship placement in ship.scss.
  shipRecenterTravel() {
    return -0.04 * $("#ship-container").width();
  }


  getMTV() {
    return $("#ship-container .mtv");
  }

  // Cross-fades the background layers to the given stage id. Called by the game
  // when a sequence action names a stage in the JSON, and by hand from the
  // transitions that have to time their own swap.
  //
  // Only the background is staged. The ship is one element for the whole run,
  // moved and turned rather than swapped, so nothing about it is here.
  setStage(stageId, instant = false) {
    if (this.currentStage === stageId) return;

    this.currentStage = stageId;

    const duration = instant ? 0 : 1;

    const $stages = $("#background-container").children(".stage");
    const $target = $stages.filter(`[data-stage="${stageId}"]`);

    // A stage the background has nothing of leaves the current one showing.
    if (!$target.length) return;

    gsap.to($target, {
      autoAlpha: 1,
      duration: duration,
      ease: "power1.inOut"
    });

    const $others = $stages.not($target);
    if (!$others.length) return;

    gsap.to($others, {
      autoAlpha: 0,
      // The new one is up before the old one goes, so the two are never both
      // half faded and the sky never dips between them.
      delay: duration,
      duration: duration,
      ease: "power1.inOut"
    });
  }

  // Play again from the conclusion: back to screen 2 for a fresh run (the
  // screen entry replays the intro and restarts the game).
  // The two choices of the conclusion both sound like a right press.
  tryAgain() {
    this.audio.play(SOUNDS.rightInput, this.getSoundVolume("buttons"), true);
    this.fadeThroughBlack(2);
  }

  closeGame() {
    this.audio.play(SOUNDS.rightInput, this.getSoundVolume("buttons"), true);
    this.reset();
  }

  // Leaving a run behind, either for a fresh one or back to the intro. The
  // states of the run take a moment to travel back on their own, so they are
  // put back instantly instead, under a black veil: fade to black, reset and
  // swap the screen with nothing showing, then fade back in on a clean screen.
  fadeThroughBlack(screenId, onBlack = null) {
    const $blackout = $("#blackout");

    // The veil is not up yet, so hold every input from the first frame.
    this.lockInteraction();

    gsap.to($blackout, {
      autoAlpha: 1,
      duration: 0.4,
      ease: "power1.inOut",
      onComplete: () => {
        this.resetState();
        if (typeof onBlack === "function") onBlack();

        // Both fades of the screen transition are skipped, the veil is the
        // only fade the player sees.
        this.goToScreen(screenId, () => {
          gsap.to($blackout, {
            autoAlpha: 0,
            duration: 0.4,
            ease: "power1.inOut"
          });
        }, true, {skipOut: true, skipIn: true, allowSameScreenTransition: true});
      }
    });
  }

  // Everything a run leaves behind, put back the way it starts, instantly and
  // with nothing animating. Only ever called behind the veil.
  resetState() {
    this.game.stop();
    this.stopDemos();

    // A control still held when the run ends would stay held forever, the
    // release event of the physical console is not guaranteed to come.
    this.heldActions = {};

    // Reset variable and timeline
    this.killTimeline(this.currentTimeline);
    this.killTimeline(this.currentTransitionTimeline);
    this.currentTransitionTimeline = null;

    if (this.shipHeatGlow) this.shipHeatGlow.kill();
    if (this.dustCloud) this.dustCloud.reset();
    // Faded rather than cut, a loop stopped mid wave clicks.
    this.audio.stop(SOUNDS.fire, 0.3);
    this.audio.stop(SOUNDS.thrusters, 0.3);

    // The dark band at the top comes back with the black sky it belongs to.
    gsap.killTweensOf(this.getOverlayMask());
    gsap.set(this.getOverlayMask(), {clearProps: "opacity,visibility"});

    // Kill everything travelling in the sky to prevent overlapping tweens, and
    // put the stars, the arcs and the atmosphere back where they started.
    gsap.killTweensOf(this.getSky());
    gsap.killTweensOf(this.getSkyLayer());
    gsap.set(this.getSky(), {clearProps: "transform"});
    gsap.set(this.getSkyLayer(), {clearProps: "opacity"});

    this.stopStarTwinkle(0);

    // The ground goes back below the screen, waiting for the next landing, and
    // the shadow with it.
    gsap.killTweensOf(this.getMarsGround());
    gsap.set(this.getMarsGround(), {clearProps: "transform,opacity,visibility"});
    gsap.killTweensOf(this.getMarsGroundShadow());
    gsap.set(this.getMarsGroundShadow(), {clearProps: "transform,opacity,visibility"});

    if (this.backgroundDrift) {
      gsap.killTweensOf(this.backgroundDrift);
      this.backgroundDrift = null;
    }

    this.skyRise = null;
    this.backgroundSpeed = 1;

    gsap.killTweensOf("#bg-stars");
    gsap.killTweensOf("#background-container .bg-lines");
    gsap.set($("#bg-stars"), {clearProps: "backgroundPosition,opacity,visibility"});
    gsap.set($("#background-container .bg-lines"), {clearProps: "transform,opacity,visibility"});

    this.game.reset();
    this.setStage(1, true);
    this.resetGauge();
    gsap.set($("#gauges-container"), {autoAlpha: 0});
    gsap.set($("#ship-container"), {autoAlpha: 1});

    // The landing transition leaves the last ship tilted and low, and the run
    // leaves the capsule assembly mid detach. Both belong to the run that just
    // ended.
    //
    // The actions destroyed by the stop above ease their own piece back to rest
    // over half a second, which would go on writing transforms after the props
    // are cleared here, so those are killed first.
    this.resetShipAssembly();

    const shipParts = $("#ship-container").find(".ship, .ship-placement, .ship-assembly, .mtv, .ship-glass-lens");
    gsap.killTweensOf(shipParts);
    gsap.set(shipParts, {clearProps: "transform,opacity"});

    // The sky is standing still again, so the stars go back to blinking.
    this.startStarTwinkle();

    // The label keeps the shadow it took on halfway through the run.
    $("#screen-2 .action-container").removeClass("on-background-color");
  }

  // The press that leaves the intro for the run, which sounds the same as the
  // one that left the idle step.
  launchGame() {
    this.audio.play(SOUNDS.startGame, this.getSoundVolume("buttons"), true);
    this.goToNextScreen();
  }

  beforeStep(stepId) {
    if (stepId === 2) {
      // Leaving the idle step is the press that starts the game.
      this.audio.play(SOUNDS.startGame, this.getSoundVolume("buttons"), true);

      this.timingDemo.startTiming();
      this.memoryDemo.startMemory();
    }
  }

  // The two demos of screen 1 loop forever once started, so they are stopped
  // when the intro is left behind. Coming back to that step starts them again.
  stopDemos() {
    if (this.timingDemo) this.timingDemo.stop();
    if (this.memoryDemo) this.memoryDemo.stop();
  }

  /**
   * Reset function, used to go back clean version of the game
   * @param screenId
   */
  reset(screenId = 1) {
    this.started = false;
    FrameAnimation.stopAll();

    // Back to the idle screen, so the language goes back to the default one
    // too. Behind the veil, the swap is never seen.
    this.fadeThroughBlack(screenId, () => {
      this.i18n.set(this.settings.default_language);
      this.audio.playLoop(SOUNDS.idle, this.getSoundVolume("idle"), 1);
    });
  }

}
